import {
  Button,
  Card,
  CreateButton,
  Divider,
  Icons,
  InputNumber,
  Radio,
  RadioChangeEvent,
  Space,
  Table,
  Typography,
  Upload,
  UploadFile,
} from '@pankod/refine-antd';
import { UploadChangeParam } from 'antd/lib/upload';
import { CustomDrawer } from 'components';
import React, { useEffect, useMemo, useState } from 'react';
import { Action, ActionKind, ISaleProductState } from 'reducers/product-sale-reducer';
import { EDiscountEntryType } from 'utilities/discount-entry';
import { IS_DISCOUNT_IN_PERCENTAGE } from 'utilities/shared';
import { Column, usePurchaseSaleCsvImport } from '../../hooks/usePurchaseSaleCsvImport';
import { AddProductForm } from './AddProductForm';
import { EditProductForm } from './EditProductForm';
import { calculateSaleTotals } from 'utilities/sale-totals';
import { LineDiscountCell } from './LineDiscountCell';

const { Text, Title } = Typography;

type Props = {
  saleProducts: ISaleProductState[];
  warehouse?: string;
  vat: number;
  shipment: number;
  discountCost: number;
  paidInCash: boolean;
  payNow: boolean;
  fullDue: boolean;
  productDispatcher: React.Dispatch<Action>;
};

export const ProductSaleTable: React.FC<Props> = ({
  saleProducts,
  warehouse,
  paidInCash,
  fullDue,
  shipment,
  vat,
  productDispatcher,
}) => {
  const [editSaleProduct, setEditSaleProduct] = useState<ISaleProductState | null>(null);
  const [isAddDrawerVisible, setAddDrawerVisible] = useState(false);
  const [isEditDrawerVisible, setEditDrawerVisible] = useState(false);
  const { isImporting, getCsvTemplate, onImportCsv } = usePurchaseSaleCsvImport();

  const handleFileUpload = async (info: UploadChangeParam<UploadFile<any>>) => {
    if (!info.fileList.length) return;
    const file = info.fileList[0].originFileObj!;
    const variantsInfo = await onImportCsv(file);
    productDispatcher({ type: ActionKind.SetSaleProducts, payload: variantsInfo });
  };

  const renderTableActions = (record: ISaleProductState) => (
    <Space>
      <Button
        size="small"
        icon={<Icons.EditOutlined />}
        onClick={() => {
          setEditSaleProduct(record);
          setEditDrawerVisible(true);
        }}
      />
      <Button
        size="small"
        danger
        icon={<Icons.DeleteOutlined />}
        onClick={() =>
          productDispatcher({
            type: ActionKind.RemoveSaleProduct,
            payload: record.productVariantId,
          })
        }
      />
    </Space>
  );

  const headerActions = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '8px',
        width: '100%',
        whiteSpace: 'normal',
      }}
    >
      <Title level={4} style={{ margin: 0 }}>
        Add product
      </Title>
      <Space wrap size={[8, 8]}>
        <CreateButton
          type="primary"
          onClick={() => setAddDrawerVisible(true)}
          disabled={!warehouse}
        >
          Add New
        </CreateButton>
        <Button onClick={getCsvTemplate} icon={<Icons.DownloadOutlined />}>
          Sample CSV
        </Button>
        <Upload
          accept="text/csv"
          maxCount={1}
          beforeUpload={() => false}
          onChange={handleFileUpload}
          onRemove={() => productDispatcher({ type: ActionKind.SetSaleProducts, payload: [] })}
        >
          <Button icon={<Icons.ImportOutlined />} loading={isImporting}>
            Import
          </Button>
        </Upload>
      </Space>
    </div>
  );

  return (
    <>
      {/* Add Drawer */}
      {isAddDrawerVisible && (
        <CustomDrawer
          title="Add Product"
          visible={isAddDrawerVisible}
          onClose={() => setAddDrawerVisible(false)}
        >
          <AddProductForm
            warehouseId={warehouse}
            saleProducts={saleProducts}
            onSubmit={(product) =>
              productDispatcher({ type: ActionKind.AddSaleProduct, payload: product })
            }
          />
        </CustomDrawer>
      )}

      {/* Edit Drawer */}
      {editSaleProduct && (
        <CustomDrawer
          title="Edit Product"
          visible={isEditDrawerVisible}
          onClose={() => setEditDrawerVisible(false)}
        >
          <EditProductForm
            editProduct={editSaleProduct}
            warehouse={warehouse}
            onSubmit={(product) => {
              productDispatcher({ type: ActionKind.EditSaleProduct, payload: product });
              setEditDrawerVisible(false);
            }}
          />
        </CustomDrawer>
      )}

      <Card title={headerActions}>
        <Table dataSource={saleProducts} rowKey="sku" scroll={{ x: true }}>
          <Table.Column dataIndex="sku" title={Column.Sku} />
          <Table.Column dataIndex="productVariantName" title={Column.Name} />
          <Table.Column dataIndex="quantity" title={Column.Quantity} />
          <Table.Column dataIndex="unitPrice" title={Column.UnitPrice} />
          <Table.Column<ISaleProductState>
            title="Discount"
            render={(_, record) => (
              <LineDiscountCell
                quantity={record.quantity}
                unitPrice={record.unitPrice}
                discountCost={record.discountCost}
                discountEntryValue={record.discountEntryValue}
                discountEntryType={record.discountEntryType}
              />
            )}
          />
          <Table.Column<ISaleProductState>
            title="Total"
            render={(_, record) => (
              <Text>
                {Number(
                  (record.quantity * record.unitPrice - (record.discountCost || 0)).toFixed(2),
                )}
              </Text>
            )}
          />
          <Table.Column<ISaleProductState>
            title="Actions"
            render={(_, record) => renderTableActions(record)}
          />
        </Table>
      </Card>

      <SalesPaymentDetails
        saleProducts={saleProducts}
        paidInCash={paidInCash}
        fullDue={fullDue}
        shipment={shipment}
        vat={vat}
        productDispatcher={productDispatcher}
      />
    </>
  );
};

type SalesPaymentDetailsProps = {
  saleProducts: ISaleProductState[];
  paidInCash: boolean;
  fullDue: boolean;
  shipment: number;
  vat: number;
  productDispatcher: React.Dispatch<Action>;
};

const SalesPaymentDetails: React.FC<SalesPaymentDetailsProps> = ({
  saleProducts,
  paidInCash,
  fullDue,
  shipment,
  vat,
  productDispatcher,
}) => {
  const [discountInput, setDiscountInput] = useState<number | null>(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [isDiscountInPercentage, setIsDiscountInPercentage] = useState<boolean>(() => {
    const data = window.localStorage.getItem(IS_DISCOUNT_IN_PERCENTAGE);
    if (data && typeof data === 'string') {
      return JSON.parse(data);
    } else {
      return false;
    }
  });

  const subTotal = saleProducts.reduce((sum, p) => sum + p.quantity * p.unitPrice, 0);

  // Memoized discountAmount for rendering
  const discountAmount = useMemo(() => {
    if (!discountInput) return 0;
    return isDiscountInPercentage
      ? Number((subTotal * (discountInput / 100)).toFixed(2))
      : Number(discountInput.toFixed(2));
  }, [discountInput, isDiscountInPercentage, subTotal]);

  // Safe pathname access
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsCreateMode(window.location.pathname.includes('create'));
    }
  }, []);

  // Debounce dispatch
  useEffect(() => {
    const timeout = setTimeout(() => {
      productDispatcher({ type: ActionKind.SetDiscount, payload: discountAmount });
      productDispatcher({
        type: ActionKind.SetInvoiceDiscountEntry,
        payload: discountInput
          ? {
              discountEntryValue: discountInput,
              discountEntryType: isDiscountInPercentage
                ? EDiscountEntryType.Percent
                : EDiscountEntryType.Amount,
            }
          : { discountEntryValue: null, discountEntryType: null },
      });
    }, 300);

    return () => clearTimeout(timeout);
  }, [productDispatcher, discountAmount, discountInput, isDiscountInPercentage]);

  const handleFullCashChange = (e: RadioChangeEvent) => {
    if (e.target.checked) {
      productDispatcher({ type: ActionKind.SetPayNow, payload: false });
      productDispatcher({ type: ActionKind.SetDue, payload: false });
    }
    productDispatcher({ type: ActionKind.SetPaidInCash, payload: e.target.checked });
  };

  const handleFullDueChange = (e: RadioChangeEvent) => {
    if (e.target.checked) {
      productDispatcher({ type: ActionKind.SetPaidInCash, payload: false });
      productDispatcher({ type: ActionKind.SetPayNow, payload: false });
    }
    productDispatcher({ type: ActionKind.SetDue, payload: e.target.checked });
  };

  const totalAmountDiscount = saleProducts.reduce((discount, product) => {
    discount += product.discountCost || 0;
    return discount;
  }, 0);
  // VAT and total exactly as the API saves them, so Full Cash pays the saved total.
  const { vatAmount, netTotal: total } = calculateSaleTotals({
    saleProducts,
    vatPercentage: vat,
    shipmentCost: shipment,
    invoiceDiscountCost: discountAmount,
  });
  return (
    <Card title="Payment Details" style={{ marginTop: '1rem' }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        {/* Discount Section */}
        <Space style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Text strong>Invoice Discount</Text>
          <Space>
            <InputNumber
              placeholder="Value"
              disabled={saleProducts.length === 0}
              min={0}
              max={isDiscountInPercentage ? 100 : subTotal}
              value={discountInput}
              onInput={(value) => {
                const numericValue = Number(value);
                if (isNaN(numericValue)) return;
                if (isDiscountInPercentage) {
                  setDiscountInput(Math.min(numericValue, 100));
                }
                if (!isDiscountInPercentage) {
                  setDiscountInput(Math.min(numericValue, subTotal));
                }
              }}
            />
            <Button
              type={isDiscountInPercentage ? 'primary' : 'default'}
              onClick={() => {
                setIsDiscountInPercentage(true);
                window.localStorage.setItem(IS_DISCOUNT_IN_PERCENTAGE, 'true');
              }}
            >
              <Icons.PercentageOutlined />
            </Button>
            <Button
              type={!isDiscountInPercentage ? 'primary' : 'default'}
              onClick={() => {
                setIsDiscountInPercentage(false);
                window.localStorage.setItem(IS_DISCOUNT_IN_PERCENTAGE, 'false');
              }}
            >
              TK
            </Button>
          </Space>
        </Space>

        <Divider />

        {/* Subtotal */}
        <Space style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Text strong>Subtotal</Text>
          <Text>TK {subTotal.toFixed(2)}</Text>
        </Space>

        {/* VAT: only while the organization rate applies (sale.vatRate) */}
        {vat > 0 && (
          <Space style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Text strong>VAT ({vat}%)</Text>
            <Text>TK {vatAmount.toFixed(2)}</Text>
          </Space>
        )}

        {/* Discount */}
        <Space style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Text strong>Total Discount</Text>
          <Text type="danger">TK {(discountAmount + totalAmountDiscount).toFixed(2)}</Text>
        </Space>

        <Divider />

        {/* Total */}
        <Space style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Text strong>Total</Text>
          <Text strong style={{ fontSize: '1.5rem' }}>
            TK {total.toFixed(2)}
          </Text>
        </Space>
      </Space>

      {/* Payment Method Radio */}
      <Space style={{ marginTop: '1rem' }}>
        {isCreateMode && (
          <span>
            <Radio onChange={handleFullCashChange} checked={paidInCash}>
              Full Cash
            </Radio>
          </span>
        )}
        <span style={{ marginLeft: '1rem' }}>
          <Radio onChange={handleFullDueChange} checked={fullDue}>
            Full Due
          </Radio>
        </span>
      </Space>
    </Card>
  );
};
